import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Bar, Pie, Line, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  TrendingUpIcon,
  UsersIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
  MapPinIcon,
  BanknotesIcon,
  ClockIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsPointingOutIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import CustomEmail from "./CustomEmail";
import EditStudent from "../EditStudent";

// Enhanced API service with error handling and caching
class ApiService {
  static cache = new Map();
  static baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  static async fetchData(endpoint, useCache = true) {
    const cacheKey = endpoint;

    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) {
        return cached.data;
      }
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${this.baseUrl}/api/${endpoint}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (useCache) {
        this.cache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  }

  static async clearOverdueStudent(userId) {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${this.baseUrl}/api/overdue-students/${userId}/clear`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error clearing overdue student:", error);
      throw error;
    }
  }

  static clearCache() {
    this.cache.clear();
  }
}

const Dashboard = () => {
  // State management
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [studentsByProgramme, setStudentsByProgramme] = useState([]);
  const [studentsByUniversity, setStudentsByUniversity] = useState([]);
  const [registrationTrends, setRegistrationTrends] = useState([]);
  const [geographicInsights, setGeographicInsights] = useState(null);
  const [applicationAnalytics, setApplicationAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [engagementMetrics, setEngagementMetrics] = useState(null);
  const [programmePerformance, setProgrammePerformance] = useState([]);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);

  // Student data states
  const [scheduledStudents, setScheduledStudents] = useState([]);
  const [todayStudents, settodayStudents] = useState([]);
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [clearingStudent, setClearingStudent] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    progress: true,
    statusDetails: true,
    scheduled: true,
    charts: true,
    activity: false,
    tables: false,
  });

  const dashboardRef = useRef(null);

  // Fetch all data
  const fetchAllData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const endpoints = [
        "dashboard-summary",
        "students-by-programme",
        "students-by-university",
        "registration-trends",
        "geographic-insights",
        "application-analytics",
        "recent-activity?limit=20",
        "engagement-metrics",
        "programme-student-count",
        "scheduled-students?days=7",
        "admin/today-scheduled-students",
        "admin/overdue-students",
      ];

      const results = await Promise.allSettled(
        endpoints.map((endpoint) =>
          ApiService.fetchData(endpoint, !showLoading)
        )
      );

      const [
        summaryResult,
        programmeResult,
        universityResult,
        trendsResult,
        geographicResult,
        applicationResult,
        activityResult,
        engagementResult,
        performanceResult,
        scheduledResult,
        todayScheduledResult,
        overdueResult,
      ] = results;

      if (summaryResult.status === "fulfilled")
        setDashboardSummary(summaryResult.value);
      if (programmeResult.status === "fulfilled")
        setStudentsByProgramme(programmeResult.value || []);
      if (universityResult.status === "fulfilled")
        setStudentsByUniversity(universityResult.value || []);
      if (trendsResult.status === "fulfilled")
        setRegistrationTrends(trendsResult.value || []);
      if (geographicResult.status === "fulfilled")
        setGeographicInsights(geographicResult.value);
      if (applicationResult.status === "fulfilled")
        setApplicationAnalytics(applicationResult.value);
      if (activityResult.status === "fulfilled")
        setRecentActivity(activityResult.value || []);
      if (engagementResult.status === "fulfilled")
        setEngagementMetrics(engagementResult.value);
      if (performanceResult.status === "fulfilled")
        setProgrammePerformance(performanceResult.value || []);
      if (scheduledResult.status === "fulfilled")
        setScheduledStudents(scheduledResult.value || []);
      if (overdueResult.status === "fulfilled") {
        const overdueData = overdueResult.value;
        setOverdueStudents(overdueData?.all_overdue_students || []);
      }
      if (todayScheduledResult.status === "fulfilled") {
        const todayData = todayScheduledResult.value;
        settodayStudents(todayData?.students || []);
      }

      const failedRequests = results.filter(
        (result) => result.status === "rejected"
      ).length;
      if (failedRequests > 0) {
        console.warn(`${failedRequests} API requests failed`);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data. Please try refreshing.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Refresh data function
  const refreshData = useCallback(() => {
    setRefreshing(true);
    ApiService.clearCache();
    fetchAllData(false);
  }, [fetchAllData]);

  // Handle clearing overdue student
  const handleClearOverdue = async (userId, studentName) => {
    if (!userId) {
      alert("Invalid student ID");
      return;
    }

    if (!window.confirm(`Mark ${studentName} as resolved and remove from overdue list?`)) {
      return;
    }

    setClearingStudent(userId);
    
    try {
      await ApiService.clearOverdueStudent(userId);
      
      // Remove the student from the local state immediately
      setOverdueStudents(prev => prev.filter(student => student.user_id !== userId));
      
      // Refresh the data to get updated counts
      refreshData();
      
      // Show success message
      alert(`${studentName} has been removed from the overdue list successfully!`);
    } catch (error) {
      console.error("Error clearing overdue student:", error);
      alert(`Failed to mark ${studentName} as resolved. Please try again.`);
    } finally {
      setClearingStudent(null);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(refreshData, 300000);
    return () => clearInterval(interval);
  }, [refreshData]);

  // Enhanced PDF Export
  const exportComprehensivePDF = async () => {
    setExportingPDF(true);
    try {
      if (!dashboardRef.current) {
        alert("Dashboard content not available for export");
        return;
      }

      // Create canvas from dashboard element
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        allowTaint: true,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add image to PDF, creating new pages if needed
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= 297; // A4 height in mm

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= 297;
      }

      pdf.save(`analytics-report-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF report. Please try again.");
    } finally {
      setExportingPDF(false);
    }
  };

  // Quick actions
  const quickActions = [
    {
      label: "Refresh Data",
      icon: ArrowPathIcon,
      action: refreshData,
      color: "gray",
    },
    {
      label: "Export PDF",
      icon: ArrowDownTrayIcon,
      action: exportComprehensivePDF,
      color: "blue",
    },
    {
      label: "View Alerts",
      icon: ExclamationTriangleIcon,
      action: () => setActiveFilter("urgent"),
      color: "red",
    },
  ];

  // Filter students based on search and active filter
  const filteredTodayStudents = useMemo(() => {
    return todayStudents.filter(student => {
      const matchesSearch = searchQuery === "" || 
        student.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = activeFilter === "all" || 
        (activeFilter === "urgent" && student.days_until_scheduled === 0);
      
      return matchesSearch && matchesFilter;
    });
  }, [todayStudents, searchQuery, activeFilter]);

  const filteredScheduledStudents = useMemo(() => {
    return scheduledStudents.filter(student => {
      const matchesSearch = searchQuery === "" || 
        student.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [scheduledStudents, searchQuery]);

  const filteredOverdueStudents = useMemo(() => {
    return overdueStudents.filter(student => {
      const matchesSearch = searchQuery === "" || 
        student.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.last_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [overdueStudents, searchQuery]);

  // Color palette for charts
  const colors = [
    "#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#6366F1",
    "#06B6D4", "#8B5CF6", "#F43F5E", "#22C55E", "#EAB308",
  ];

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 11 },
          color: "#374151",
          usePointStyle: true,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: { size: 9 },
          color: "#6B7280",
          maxRotation: 45,
        },
        grid: { color: "#F3F4F6" },
      },
      y: {
        ticks: { color: "#6B7280", font: { size: 9 } },
        grid: { color: "#F3F4F6" },
      },
    },
  };

  const mobileChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        position: "bottom",
        labels: {
          font: { size: 10 },
          boxWidth: 12,
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          font: { size: 10 },
          color: "#374151",
          usePointStyle: true,
          boxWidth: 10,
        },
      },
    },
  };

  // Chart data preparation
  const registrationTrendData = useMemo(() => {
    if (!registrationTrends?.length) return { labels: [], datasets: [] };
    return {
      labels: registrationTrends.map((item) => item.month),
      datasets: [
        {
          label: "Total Registrations",
          data: registrationTrends.map((item) => item.totalRegistrations || 0),
          borderColor: "#4F46E5",
          backgroundColor: "rgba(79, 70, 229, 0.1)",
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [registrationTrends]);

  const nationalityData = useMemo(() => {
    if (!geographicInsights?.byNationality?.length) return { labels: [], datasets: [] };
    const top8 = geographicInsights.byNationality.slice(0, 8);
    return {
      labels: top8.map((item) => item.country),
      datasets: [{
        data: top8.map((item) => item.count || 0),
        backgroundColor: colors.slice(0, 8),
        borderWidth: 2,
      }],
    };
  }, [geographicInsights]);

  const statusDistributionData = useMemo(() => {
    if (!applicationAnalytics?.statusDistribution?.length) return { labels: [], datasets: [] };
    return {
      labels: applicationAnalytics.statusDistribution.map((item) => item.status),
      datasets: [{
        data: applicationAnalytics.statusDistribution.map((item) => item.count || 0),
        backgroundColor: colors,
        borderWidth: 2,
      }],
    };
  }, [applicationAnalytics]);

  const programmePerformanceData = useMemo(() => {
    if (!programmePerformance?.length) return { labels: [], datasets: [] };
    const top10 = programmePerformance.slice(0, 10);
    return {
      labels: top10.map((item) => {
        const name = item.programme || "Unknown";
        return name.length > 15 ? name.substring(0, 15) + "..." : name;
      }),
      datasets: [
        {
          label: "Applicants",
          data: top10.map((item) => item.totalApplicants || 0),
          backgroundColor: "#60A5FA",
          borderRadius: 4,
        },
      ],
    };
  }, [programmePerformance]);

  const universityDistributionData = useMemo(() => {
    if (!studentsByUniversity?.length) return { labels: [], datasets: [] };
    const top8 = studentsByUniversity.slice(0, 8);
    return {
      labels: top8.map((item) => {
        const name = item.university || "Unknown";
        return name.length > 12 ? name.substring(0, 12) + "..." : name;
      }),
      datasets: [{
        label: "Students",
        data: top8.map((item) => item.studentCount || 0),
        backgroundColor: colors.slice(0, 8),
        borderRadius: 4,
      }],
    };
  }, [studentsByUniversity]);

  // Handlers
  const handleTakeAction = (studentId) => {
    localStorage.setItem("student_id", studentId);
    setIsEmailOpen(true);
  };

  const handleCloseModal = () => {
    localStorage.removeItem("student_id");
    setIsEmailOpen(false);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleAllSections = (expand) => {
    setExpandedSections({
      summary: expand,
      progress: expand,
      statusDetails: expand,
      scheduled: expand,
      charts: expand,
      activity: expand,
      tables: expand,
    });
  };

  // Error boundary component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-red-800 mb-2">
        <ExclamationTriangleIcon className="h-5 w-5" />
        <span className="font-medium">Error Loading Dashboard</span>
      </div>
      <p className="text-red-700 text-sm mb-3">{message}</p>
      <button
        onClick={onRetry}
        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  // Loading component
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4">
        <div className="max-w-2xl mx-auto mt-10">
          <ErrorMessage message={error} onRetry={() => fetchAllData()} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50" ref={dashboardRef}>
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-40 bg-white shadow-md border-b p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Analytics</h1>
            <p className="text-xs text-gray-600 mt-1">Application & Enrollment Insights</p>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg bg-gray-100"
          >
            <div className="w-6 h-6 flex flex-col justify-center">
              <span className={`w-full h-0.5 bg-gray-600 transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-1' : ''}`}></span>
              <span className={`w-full h-0.5 bg-gray-600 transition-all mt-1 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-full h-0.5 bg-gray-600 transition-all mt-1 ${mobileMenuOpen ? '-rotate-45 -translate-y-1' : ''}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Quick Actions */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-4 grid grid-cols-3 gap-2"
          >
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={action.action}
                className={`p-3 rounded-lg text-center text-xs font-medium ${
                  action.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                  action.color === 'red' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}
              >
                <action.icon className="h-4 w-4 mx-auto mb-1" />
                {action.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {/* Main Content */}
      <div className="container mx-auto p-3 lg:p-6">
        {/* Enhanced Header */}
        <div className="hidden lg:flex flex-col lg:flex-row items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">Application Analytics</h1> 
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              <span>📅 Last updated: {new Date().toLocaleString()}</span>
              {refreshing && (
                <div className="flex items-center gap-1">
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                  <span>Refreshing...</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => toggleAllSections(true)}
              className="inline-flex items-center gap-1 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-sm"
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
              Expand All
            </button>
            <button
              onClick={() => toggleAllSections(false)}
              className="inline-flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-sm"
            >
              <ChevronUpIcon className="h-4 w-4" />
              Collapse All
            </button>
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="inline-flex items-center gap-1 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-sm"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={exportComprehensivePDF}
              disabled={exportingPDF}
              className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 rounded-lg shadow-sm"
            >
              {exportingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Export PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border p-3 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Students</option>
                <option value="urgent">Urgent Only</option>
              </select>
              <button
                onClick={() => setSearchQuery("")}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <AnimatePresence>
          {dashboardSummary && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <SectionHeader
                title="System Overview"
                expanded={expandedSections.summary}
                onToggle={() => toggleSection("summary")}
              />
              {expandedSections.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <SummaryCard
                    title="Total Users"
                    value={dashboardSummary.overview.totalUsers}
                    change={dashboardSummary.growth.newUsersThisMonth}
                    changeLabel="new this month"
                    icon={UsersIcon}
                    color="blue"
                  />
                  <SummaryCard
                    title="Active Students"
                    value={dashboardSummary.overview.totalStudents}
                    change={dashboardSummary.growth.newStudentsThisMonth}
                    changeLabel="new this month"
                    icon={AcademicCapIcon}
                    color="green"
                  />
                  <SummaryCard
                    title="Universities"
                    value={dashboardSummary.overview.totalUniversities}
                    icon={BuildingOfficeIcon}
                    color="purple"
                  />
                  <SummaryCard
                    title="Documents"
                    value={dashboardSummary.overview.totalDocuments}
                    icon={DocumentTextIcon}
                    color="orange"
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Student Scheduling Overview */}
        <AnimatePresence>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <SectionHeader
              title="Student Scheduling"
              expanded={expandedSections.scheduled}
              onToggle={() => toggleSection("scheduled")}
            />
            {expandedSections.scheduled && (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                {/* Today's Students */}
                <StudentCard
                  title="Today's Students"
                  students={filteredTodayStudents}
                  icon={CalendarDaysIcon}
                  iconColor="text-red-600"
                  bgColor="bg-red-50"
                  count={filteredTodayStudents.length}
                  emptyMessage="No students scheduled for today"
                  onTakeAction={handleTakeAction}
                  showUrgency
                />

                {/* Scheduled Students */}
                <StudentCard
                  title="Scheduled (7 Days)"
                  students={filteredScheduledStudents.slice(0, 5)}
                  icon={ClockIcon}
                  iconColor="text-blue-600"
                  bgColor="bg-blue-50"
                  count={scheduledStudents.length}
                  emptyMessage="No scheduled students"
                />

                {/* Overdue Students */}
                <StudentCard
                  title="Overdue Students"
                  students={filteredOverdueStudents.slice(0, 5)}
                  icon={ExclamationCircleIcon}
                  iconColor="text-orange-600"
                  bgColor="bg-orange-50"
                  count={overdueStudents.length}
                  emptyMessage="No overdue students"
                  showOverdue
                  onClearOverdue={handleClearOverdue}
                  clearingStudent={clearingStudent}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Application Progress */}
        <AnimatePresence>
          {dashboardSummary?.applicationProgress && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <SectionHeader
                title="Application Progress"
                expanded={expandedSections.progress}
                onToggle={() => toggleSection("progress")}
              />
              {expandedSections.progress && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ProgressCard
                    title="Personal Info"
                    completed={dashboardSummary.applicationProgress.usersWithPersonalInfo}
                    total={dashboardSummary.overview.totalUsers}
                    percentage={dashboardSummary.applicationProgress.completionRates.personalInfo}
                    color="blue"
                  />
                  <ProgressCard
                    title="Course Details"
                    completed={dashboardSummary.applicationProgress.usersWithCourseDetails}
                    total={dashboardSummary.overview.totalUsers}
                    percentage={dashboardSummary.applicationProgress.completionRates.courseDetails}
                    color="green"
                  />
                  <ProgressCard
                    title="Documents"
                    completed={dashboardSummary.applicationProgress.usersWithDocuments}
                    total={dashboardSummary.overview.totalUsers}
                    percentage={dashboardSummary.applicationProgress.completionRates.documents}
                    color="purple"
                  />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Analytics Charts */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <SectionHeader
            title="Analytics Charts"
            expanded={expandedSections.charts}
            onToggle={() => toggleSection("charts")}
          />
          {expandedSections.charts && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Registration Trends" responsive>
                <Line 
                  data={registrationTrendData} 
                  options={window.innerWidth < 768 ? mobileChartOptions : chartOptions} 
                />
              </ChartCard>
              <ChartCard title="Students by Nationality" responsive>
                <Pie data={nationalityData} options={pieOptions} />
              </ChartCard>
              <ChartCard title="Application Status" responsive>
                <Doughnut data={statusDistributionData} options={pieOptions} />
              </ChartCard>
              <ChartCard title="Programme Performance" responsive>
                <Bar 
                  data={programmePerformanceData} 
                  options={window.innerWidth < 768 ? mobileChartOptions : chartOptions} 
                />
              </ChartCard>
              <ChartCard title="University Distribution" responsive>
                <Bar 
                  data={universityDistributionData} 
                  options={window.innerWidth < 768 ? mobileChartOptions : chartOptions} 
                />
              </ChartCard>
            </div>
          )}
        </motion.div>

        {/* Application Status Details */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <SectionHeader
            title="Application Status Details"
            expanded={expandedSections.statusDetails}
            onToggle={() => toggleSection("statusDetails")}
          />
          {expandedSections.statusDetails && applicationAnalytics && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Status Distribution */}
              <MetricCard
                title="Application Status"
                icon={DocumentTextIcon}
                iconColor="text-purple-600"
                metrics={(applicationAnalytics.statusDistribution || []).slice(0, 3).map(s => ({
                  label: s.status || "Unknown",
                  value: `${s.count} (${s.percentage}%)`,
                  color: s.count > 100 ? "green" : "orange"
                }))}
              />
              {/* Completion Progress */}
              <MetricCard
                title="Completion Progress"
                icon={CheckCircleIcon}
                iconColor="text-green-600"
                metrics={[
                  { label: "Personal Info Complete", value: `${applicationAnalytics.conversionFunnel?.personalInfoPercent || 0}%`, color: "blue" },
                  { label: "Course Details Complete", value: `${applicationAnalytics.conversionFunnel?.courseDetailsPercent || 0}%`, color: "indigo" },
                  { label: "Documents Submitted", value: `${applicationAnalytics.conversionFunnel?.documentsPercent || 0}%`, color: "green" },
                ]}
              />
              {/* User Engagement */}
              {engagementMetrics && (
                <MetricCard
                  title="Action Required"
                  icon={ExclamationCircleIcon}
                  iconColor="text-red-600"
                  metrics={[
                    { label: "Incomplete Profiles", value: engagementMetrics.incompleteProfiles?.toLocaleString() || "0", color: "red" },
                    { label: "Missing Documents", value: engagementMetrics.usersWithoutDocuments?.toLocaleString() || "0", color: "orange" },
                    { label: "Active Messages (30d)", value: engagementMetrics.messaging?.activeMessageUsers?.toLocaleString() || "0", color: "blue" },
                  ]}
                />
              )}
            </div>
          )}
        </motion.div>

        {/* Recent Activity */}
        <AnimatePresence>
          {recentActivity.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              <SectionHeader
                title="Recent Activity"
                expanded={expandedSections.activity}
                onToggle={() => toggleSection("activity")}
              />
              {expandedSections.activity && (
                <div className="bg-white rounded-xl shadow-sm border p-4">
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {recentActivity.slice(0, 8).map((activity, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          activity.activity_type === "user_registered" ? "bg-blue-500" :
                          activity.activity_type === "student_enrolled" ? "bg-green-500" : "bg-purple-500"
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 font-medium truncate">
                            {activity.details}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(activity.activity_time).toLocaleString()}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Email Modal */}
      {isEmailOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Send Email</h3>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-800">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <CustomEmail studentId={editingStudentId} onClose={handleCloseModal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Helper Components
const SectionHeader = ({ title, expanded, onToggle }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    <button onClick={onToggle} className="text-gray-500 hover:text-gray-700 p-1">
      {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
    </button>
  </div>
);

const SummaryCard = ({ title, value, change, changeLabel, icon: Icon, color }) => {
  const colorClasses = {
    blue: "text-blue-700 bg-blue-50",
    green: "text-green-700 bg-green-50",
    purple: "text-purple-700 bg-purple-50",
    orange: "text-orange-700 bg-orange-50",
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-all"
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorClasses[color].split(' ')[1]}`}>
          <Icon className={`h-5 w-5 ${colorClasses[color].split(' ')[0]}`} />
        </div>
        {change > 0 && (
          <div className="flex items-center text-green-600 text-xs">
            <ArrowUpIcon className="h-3 w-3" />
            <span className="font-medium">+{change}</span>
          </div>
        )}
      </div>
      <h3 className="text-xs font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-xl font-bold text-gray-900">
        {value?.toLocaleString() || 0}
      </p>
      {changeLabel && (
        <p className="text-xs text-gray-500 mt-1">{changeLabel}</p>
      )}
    </motion.div>
  );
};

const ProgressCard = ({ title, completed, total, percentage, color }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-all"
    >
      <h4 className="text-sm font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-600 mb-2">
          <span>{completed?.toLocaleString() || 0} of {total?.toLocaleString() || 0}</span>
          <span className="font-medium">{percentage || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${percentage || 0}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-2 rounded-full bg-gradient-to-r ${colorClasses[color]} shadow-sm`}
          />
        </div>
      </div>
      <div className="text-xs text-gray-500">Completion rate</div>
    </motion.div>
  );
};

const ChartCard = ({ title, children, responsive = false }) => (
  <motion.div
    whileHover={{ scale: 1.01 }}
    className={`bg-white rounded-lg shadow-sm border p-4 ${
      responsive ? "h-64 sm:h-72 lg:h-80" : "h-64"
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="h-[calc(100%-2rem)]">{children}</div>
  </motion.div>
);

const StudentCard = ({ 
  title, 
  students, 
  icon: Icon, 
  iconColor, 
  bgColor, 
  count, 
  emptyMessage, 
  onTakeAction, 
  showUrgency = false,
  showOverdue = false,
  onClearOverdue,
  clearingStudent = null
}) => (
  <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
    <div className={`p-3 border-b ${bgColor}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          {title}
        </h3>
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
          showOverdue ? "bg-orange-100 text-orange-800" : "bg-blue-100 text-blue-800"
        }`}>
          {count}
        </span>
      </div>
    </div>
    <div className="p-3">
      {students.length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {students.map((student, index) => (
            <motion.div
              key={student.student_id || index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`p-2 rounded-lg text-sm ${
                showUrgency ? "bg-red-50 border-l-4 border-red-500" : "bg-gray-50"
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {student.first_name} {student.last_name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {student.email}
                  </p>
                  {student.scheduled_date && (
                    <p className="text-xs text-gray-500">
                      {new Date(student.scheduled_date).toLocaleDateString()}
                      {showUrgency && " • TODAY"}
                    </p>
                  )}
                </div>
                {onTakeAction && (
                  <button
                    onClick={() => onTakeAction(student.student_number)}
                    className="ml-2 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Action
                  </button>
                )}
                {onClearOverdue && (
                  <button
                    onClick={() => onClearOverdue(student.user_id, `${student.first_name} ${student.last_name}`)}
                    disabled={clearingStudent === student.user_id}
                    className="ml-2 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    {clearingStudent === student.user_id ? (
                      <>
                        <ArrowPathIcon className="h-3 w-3 animate-spin" />
                        ...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-3 w-3" />
                        Resolved
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-gray-500 text-sm">{emptyMessage}</p>
        </div>
      )}
    </div>
  </div>
);

const MetricCard = ({ title, icon: Icon, iconColor, metrics }) => (
  <div className="bg-white rounded-lg shadow-sm border p-4">
    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
      <Icon className={`h-4 w-4 ${iconColor}`} />
      {title}
    </h3>
    <div className="space-y-2">
      {metrics.map((metric, index) => (
        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
          <span className="text-xs text-gray-600">{metric.label}</span>
          <span className={`text-sm font-bold ${
            metric.color === 'green' ? 'text-green-600' :
            metric.color === 'blue' ? 'text-blue-600' :
            metric.color === 'purple' ? 'text-purple-600' :
            metric.color === 'orange' ? 'text-orange-600' : 'text-red-600'
          }`}>
            {metric.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default Dashboard;